/**
 * Personalization maps — deterministic role × primary_goal lookup tables
 * that drive Onboarding Personalization v1 across 5 surfaces:
 *   1. Silent Home-featured Learn path card
 *   2. Default chat tab on first session
 *   3. Starter Bible book / chapter on first Bible Reader open
 *   4. Home Dashboard card order on first visit
 *   5. Announced onboarding-completion card (announce copy + starter prompt)
 *
 * Generated from specs/onboarding-personalization-cells-draft.md 2026-04-21.
 * Mason-reviewed and approved as-is. When editing cells: update THIS file,
 * not the draft (the draft is historical).
 *
 * Legend:
 *   Role keys: new_believer, growing_christian, small_group_leader,
 *              pastor_teacher, seminary_student, other
 *   Goal keys: deepen_faith, understand_bible, defend_faith, sermon_prep,
 *              answer_questions, explore_christianity
 *   Chat tabs: general, apologetics, sermon_prep, bible_study
 */

// ── Table 1 — LEARN_PATH_MAP (36 cells) ─────────────────────────
// Silent "Start here" Learn path pinned on Home Dashboard.

export const LEARN_PATH_MAP = {
  new_believer: {
    deepen_faith: 'christianity-101',
    understand_bible: 'how-to-read-the-bible',
    defend_faith: 'core-beliefs',
    sermon_prep: 'christianity-101',
    answer_questions: 'core-beliefs',
    explore_christianity: 'christianity-101',
  },
  growing_christian: {
    deepen_faith: 'core-beliefs',
    understand_bible: 'how-to-read-the-bible',
    defend_faith: 'defending-the-resurrection',
    sermon_prep: 'how-to-read-the-bible',
    answer_questions: 'core-beliefs',
    explore_christianity: 'christianity-101',
  },
  small_group_leader: {
    deepen_faith: 'core-beliefs',
    understand_bible: 'how-to-read-the-bible',
    defend_faith: 'defending-the-resurrection',
    sermon_prep: 'understanding-john',
    answer_questions: 'core-beliefs',
    explore_christianity: 'christianity-101',
  },
  pastor_teacher: {
    deepen_faith: 'understanding-romans',
    understand_bible: 'how-to-read-the-bible',
    defend_faith: 'defending-the-resurrection',
    sermon_prep: 'understanding-romans',
    answer_questions: 'problem-of-evil',
    explore_christianity: 'core-beliefs',
  },
  seminary_student: {
    deepen_faith: 'understanding-romans',
    understand_bible: 'how-to-read-the-bible',
    defend_faith: 'problem-of-evil',
    sermon_prep: 'understanding-romans',
    answer_questions: 'science-and-faith',
    explore_christianity: 'core-beliefs',
  },
  other: {
    deepen_faith: 'christianity-101',
    understand_bible: 'how-to-read-the-bible',
    defend_faith: 'core-beliefs',
    sermon_prep: 'christianity-101',
    answer_questions: 'core-beliefs',
    explore_christianity: 'christianity-101',
  },
}

// ── Table 2 — CHAT_TAB_MAP (6 cells, role-only) ─────────────────

export const CHAT_TAB_MAP = {
  new_believer: 'bible_study',
  growing_christian: 'general',
  small_group_leader: 'sermon_prep',
  pastor_teacher: 'sermon_prep',
  seminary_student: 'bible_study',
  other: 'general',
}

// ── Table 3 — BIBLE_BOOK_MAP (6 cells, role-only) ───────────────

export const BIBLE_BOOK_MAP = {
  new_believer: { book: 'John', chapter: 1 },
  growing_christian: { book: 'Romans', chapter: 1 },
  small_group_leader: { book: 'James', chapter: 1 },
  pastor_teacher: { book: 'Ephesians', chapter: 1 },
  seminary_student: { book: 'Romans', chapter: 1 },
  other: { book: 'John', chapter: 1 },
}

// ── Table 4 — HOME_ORDER_MAP (6 cells, role-only) ───────────────
// Card IDs: learn_path_featured, devotional, verse_of_day, streak, continue

export const HOME_ORDER_MAP = {
  new_believer: ['learn_path_featured', 'devotional', 'verse_of_day', 'streak', 'continue'],
  growing_christian: ['continue', 'devotional', 'learn_path_featured', 'verse_of_day', 'streak'],
  small_group_leader: ['learn_path_featured', 'continue', 'verse_of_day', 'devotional', 'streak'],
  pastor_teacher: ['learn_path_featured', 'verse_of_day', 'continue', 'devotional', 'streak'],
  seminary_student: ['learn_path_featured', 'verse_of_day', 'devotional', 'continue', 'streak'],
  other: ['devotional', 'verse_of_day', 'learn_path_featured', 'continue', 'streak'],
}

// ── Table 5 — COMPLETION_CARD_MAP (36 cells) ────────────────────
// The announced onboarding-completion card. Each cell:
//   announceCopy    — the 1-2 sentence pitch referencing role + goal
//   ctaLabel        — the button text
//   starterPrompt   — pre-filled in the Chat message box, ready to send or edit
//   destinationTab  — which chat tab opens

export const COMPLETION_CARD_MAP = {
  // ── new_believer ─────────────────────────────────────────────
  new_believer: {
    deepen_faith: {
      announceCopy:
        "You said you're new to faith and want to grow in it. The fastest way is actually to start a real conversation about where you are today.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I just became a Christian recently and I want to grow in my faith. Can you help me understand what 'deepening my faith' actually looks like day to day? I don't really know where to start.",
      destinationTab: 'bible_study',
    },
    understand_bible: {
      announceCopy:
        "You want to understand the Bible. Good news — the best place to start isn't a Sunday school lesson, it's asking the actual questions you have.",
      ctaLabel: 'Ask your first question →',
      starterPrompt:
        "I want to understand the Bible but I'm new. Where should I start reading, and what should I know before I open it?",
      destinationTab: 'bible_study',
    },
    defend_faith: {
      announceCopy:
        "You're new to faith and people are asking questions you can't answer yet. That's normal, and we can work through the common ones together.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm new to Christianity and my friends and family keep asking me questions I can't answer yet. Can you help me think through the most common ones I'll get hit with?",
      destinationTab: 'apologetics',
    },
    sermon_prep: {
      announceCopy:
        "You're new to faith but curious about teaching others someday. Best move is to build your own foundation first — let's start there.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm new to faith but I'm curious about teaching others later. What should I be learning first before I think about teaching?",
      destinationTab: 'general',
    },
    answer_questions: {
      announceCopy:
        "You have questions. The best thing you can do right now is ask one — doesn't have to be perfect.",
      ctaLabel: 'Ask your first question →',
      starterPrompt:
        "I'm new to Christianity and I have a lot of questions. Let me start with a big one: if God is good, why is there so much suffering in the world?",
      destinationTab: 'general',
    },
    explore_christianity: {
      announceCopy:
        "You're exploring Christianity — you haven't committed, and nobody's pressuring you. Let's just talk about what Christians actually claim and you can decide from there.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm exploring Christianity but haven't committed yet. Can you help me understand what Christians actually believe, without assuming I already agree?",
      destinationTab: 'general',
    },
  },

  // ── growing_christian ────────────────────────────────────────
  growing_christian: {
    deepen_faith: {
      announceCopy:
        "You said you've been a Christian for a while and want to go deeper. The honest place to start is wherever you feel stuck right now.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I've been a Christian for a while but I feel kind of stuck. What does it actually mean to 'go deeper,' and how do I know I'm making progress?",
      destinationTab: 'general',
    },
    understand_bible: {
      announceCopy:
        "You want to understand the Bible better — not just read it, but actually see what's there. Let's pick up a passage together.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I want to understand the Bible better. How do I study a passage so I'm actually getting what the author meant, not just what I want it to say?",
      destinationTab: 'bible_study',
    },
    defend_faith: {
      announceCopy:
        "You've got people in your life asking tough questions about faith. Let's get you sharper at handling them.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "Someone in my life doesn't believe and keeps asking me questions I can't answer well. Can you help me get sharper at this?",
      destinationTab: 'apologetics',
    },
    sermon_prep: {
      announceCopy:
        "You're starting to teach or share in smaller settings. Let's work together on what preparing a talk actually looks like.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm starting to teach or share in small settings. Can you help me build a short talk from a Scripture passage — from outline to delivery?",
      destinationTab: 'sermon_prep',
    },
    answer_questions: {
      announceCopy:
        "You've got theological questions that have been sitting with you. Let's take them one at a time.",
      ctaLabel: 'Ask your first question →',
      starterPrompt:
        "I've got some theological questions I've been holding onto for a while. Can we work through them one at a time?",
      destinationTab: 'general',
    },
    explore_christianity: {
      announceCopy:
        "You've been a Christian for a bit but you're still exploring — that's more common than people admit. Let's work on what you want to solidify.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I've been a Christian for a while but I still feel like I'm exploring. Where should someone go who wants to solidify what they believe?",
      destinationTab: 'general',
    },
  },

  // ── small_group_leader ───────────────────────────────────────
  small_group_leader: {
    deepen_faith: {
      announceCopy:
        "You lead others, which means your own faith needs depth before you can give it away. Let's talk about feeding yourself.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I lead a small group but I'm feeling dry myself. Can you help me find a rhythm for feeding my own faith, not just preparing for others?",
      destinationTab: 'general',
    },
    understand_bible: {
      announceCopy:
        "You teach groups, so you need to understand the Bible well enough to lead others through it. Let's sharpen the interpretive tools.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        'I lead Bible studies but I want to teach with more depth. Where should I start — hermeneutics, a specific book, both?',
      destinationTab: 'bible_study',
    },
    defend_faith: {
      announceCopy:
        "Small group leaders field the toughest questions. Let's start with the objections you're most likely to hear.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "My group asks tough questions I'm not always ready for. What are the top 3 objections I should be prepared to answer well?",
      destinationTab: 'apologetics',
    },
    sermon_prep: {
      announceCopy:
        "You're preparing a study for your group. Let's work on the passage together so you lead something worth their time.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm preparing a study on a passage for my group. Can you help me work through it — observation, interpretation, application — so I lead something worth their time?",
      destinationTab: 'sermon_prep',
    },
    answer_questions: {
      announceCopy:
        "You lead a group, which means you're fielding tough questions every week. Let's start with one you keep getting stuck on.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I lead a small group and I need better answers to the tough questions I get asked. Let's start with one I keep running into: what should I say when people ask why God allows suffering?",
      destinationTab: 'apologetics',
    },
    explore_christianity: {
      announceCopy:
        "You lead a group while you're still exploring yourself. That's honest, and you're not alone. Let's start with re-grounding the basics.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I lead a small group but I'm still exploring my own faith. Where should a leader go who wants to re-ground what they believe from the ground up?",
      destinationTab: 'general',
    },
  },

  // ── pastor_teacher ───────────────────────────────────────────
  pastor_teacher: {
    deepen_faith: {
      announceCopy:
        "You're a pastor — you know the risk of going dry. Let's talk about rhythms that feed your own faith outside of sermon prep.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm a pastor and I know the risk of getting spiritually dry. Can you help me find a rhythm that feeds my own faith outside of sermon prep?",
      destinationTab: 'general',
    },
    understand_bible: {
      announceCopy:
        "You preach weekly. Let's sharpen your interpretive instincts on a tough passage — good hermeneutics compound.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I preach weekly. Can you help me re-sharpen my interpretive instincts? Pick a passage and walk me through how I'd handle it today.",
      destinationTab: 'bible_study',
    },
    defend_faith: {
      announceCopy:
        "Your congregation brings questions you have to answer publicly. Let's make sure you're ready for the common ones.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        'My congregation brings tough questions I have to answer publicly. What are the top apologetics topics a pastor should be publicly ready to answer well?',
      destinationTab: 'apologetics',
    },
    sermon_prep: {
      announceCopy:
        "You're preparing a sermon this week. Let's work the text. Tell me the passage and we'll start with exegesis.",
      ctaLabel: 'Start your sermon prep →',
      starterPrompt:
        "I'm preaching this Sunday and need to work through the passage. My text is [PASSAGE]. Start with exegesis.",
      destinationTab: 'sermon_prep',
    },
    answer_questions: {
      announceCopy:
        "Pastoring means fielding questions. Let's work through one you're not sure you handle well.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "Pastoring means fielding questions every week. Let's work through one I'm not sure I handle well — when someone asks about the problem of evil, what's the best frame?",
      destinationTab: 'apologetics',
    },
    explore_christianity: {
      announceCopy:
        "You lead a congregation but feel spiritually flat yourself. That's pastoral honesty, and it's worth working through.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I lead a congregation but I've been feeling spiritually flat myself. Can we work back through the basics — what Christianity is actually about at the core?",
      destinationTab: 'general',
    },
  },

  // ── seminary_student ─────────────────────────────────────────
  seminary_student: {
    deepen_faith: {
      announceCopy:
        "Academic study drifts from devotional depth if you're not careful. Let's talk about keeping both alive.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm in seminary and I've noticed academic study can drift from devotional life. How do I keep both alive at once?",
      destinationTab: 'general',
    },
    understand_bible: {
      announceCopy:
        "You're being trained to exegete. Let's do a real one — pick a tough passage and we'll work it together.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm being trained in hermeneutics but I want to get sharper. Pick a tough passage and walk me through how you'd work it.",
      destinationTab: 'bible_study',
    },
    defend_faith: {
      announceCopy:
        "Seminary exposes you to every objection. Let's start with the one most worth being ready for.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm in seminary and want to prep for apologetics conversations I'll face in ministry. What's a good starting objection to work through?",
      destinationTab: 'apologetics',
    },
    sermon_prep: {
      announceCopy:
        "You're in seminary and starting to preach. Let's build a sermon from a passage — full workflow, not just an outline.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm in seminary and starting to preach. Help me build a sermon outline from a passage — I want to learn the full workflow.",
      destinationTab: 'sermon_prep',
    },
    answer_questions: {
      announceCopy:
        "You've got questions you haven't settled yet. Seminary tends to pile them up. Let's work through one.",
      ctaLabel: 'Ask your first question →',
      starterPrompt:
        "I'm in seminary and I've got questions I haven't settled yet. Let's start with one: what's the best reading on science and faith in tension?",
      destinationTab: 'general',
    },
    explore_christianity: {
      announceCopy:
        "You're in seminary but rethinking what you believe. That's harder than it sounds — let's work through it without pretense.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm in seminary but I've been rethinking what I believe. Can we work through the core of what Christianity actually claims, without assuming I still buy all of it?",
      destinationTab: 'general',
    },
  },

  // ── other ────────────────────────────────────────────────────
  other: {
    deepen_faith: {
      announceCopy: "You want to grow in your faith. Let's talk about where you are now.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I want to grow in my faith. Where should someone who doesn't fit the usual categories start?",
      destinationTab: 'general',
    },
    understand_bible: {
      announceCopy:
        "You want to understand the Bible better. Good place to start — let's pick up a passage.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I want to understand the Bible better. What's a solid starting point for someone who's self-motivated?",
      destinationTab: 'bible_study',
    },
    defend_faith: {
      announceCopy:
        "You want to be able to defend your faith better. Let's build from the most important question first.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt: 'I want to be able to defend my faith better. Where should I start?',
      destinationTab: 'apologetics',
    },
    sermon_prep: {
      announceCopy:
        "You're thinking about teaching or preparing a study at some point. Good move to start working on it now.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm thinking about teaching or preparing a study at some point. Can you help me work through the basics of doing this well?",
      destinationTab: 'sermon_prep',
    },
    answer_questions: {
      announceCopy:
        "You've got questions. Let's work through them — start with whichever one has been sitting with you longest.",
      ctaLabel: 'Ask your first question →',
      starterPrompt:
        "I've got some questions about my faith. Let's work through them — what do you think is the most common question people have, and how would you answer it?",
      destinationTab: 'general',
    },
    explore_christianity: {
      announceCopy:
        "You're curious about Christianity. No pressure — let's just talk about what it actually claims.",
      ctaLabel: 'Start your first conversation →',
      starterPrompt:
        "I'm curious about Christianity. Can you explain what it actually claims, without assuming I've already bought in?",
      destinationTab: 'general',
    },
  },
}
